import {ReactElement, useRef} from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import useConfirmDialogContext from 'contexts/ConfirmDialogContext/useConfirmDialogContext';
import useHasOverflowX from 'hooks/useHasOverflowX';
import {Cable, SectionCable, SectionCableId} from 'interfaces/Project';
import formatNumber from 'utils/formatNumber';
import AddCableDialog from './components/AddCableDialog';
import CableUnits from './components/CableUnits';

type Props = {
  deleteTrayFromSection: () => void;
  hasSelectedTray: boolean;
  sectionCables: SectionCable[];
  onCableDelete: (sectionCableId: SectionCableId) => void;
  onCableSelect: (cable: Cable) => void;
  onCableUnitsChange: (sectionCableId: SectionCableId, units: number) => void;
};

const headerCellSx = {
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '0.17px',
  color: 'ctaDark',
  borderColor: 'borderColor'
};

const cellSx = {fontSize: 14, color: 'ctaDark', borderColor: 'borderColor'};

export default function SectionCables({
  deleteTrayFromSection,
  hasSelectedTray,
  onCableUnitsChange,
  onCableDelete,
  onCableSelect,
  sectionCables
}: Props): ReactElement {
  const {t} = useTranslation('common');
  const shouldShowConfirmDialog = useRef(hasSelectedTray);
  const openConfirmDialog = useConfirmDialogContext();

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const hasHorizontalScroll = useHasOverflowX(tableContainerRef, [sectionCables]);

  const stickyActionSx = {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    ...(hasHorizontalScroll && {borderLeft: '1px solid', borderLeftColor: 'borderColor'})
  };

  function handleCableDelete(sectionCableId: string) {
    if (shouldShowConfirmDialog.current) {
      openConfirmDialog({
        onConfirm: function () {
          shouldShowConfirmDialog.current = false;
          deleteTrayFromSection();
          onCableDelete(sectionCableId);
        },
        title: t('common:confirmResetTray.title'),
        text: t('common:confirmResetTray.text')
      });
    } else {
      onCableDelete(sectionCableId);
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', sm: 'row'},
          alignItems: {xs: 'flex-start', sm: 'center'},
          justifyContent: 'space-between',
          gap: {xs: 1.5, sm: 2},
          mb: 1.5
        }}
      >
        <Typography sx={{fontSize: 18, fontWeight: 500, lineHeight: 1.5, color: 'ctaDark'}}>
          {t('common:selectedCables')}
        </Typography>
        <AddCableDialog
          defaultManufacturer={sectionCables[0]?.manufacturer}
          deleteTrayFromSection={deleteTrayFromSection}
          hasSelectedTray={hasSelectedTray}
          onCableSelect={onCableSelect}
        />
      </Box>
      {sectionCables.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 56,
            p: 3,
            border: '1px solid',
            borderColor: 'borderColor',
            borderRadius: '4px'
          }}
        >
          <Typography sx={{color: 'text.secondary'}}>{t('common:noCablesSelected')}</Typography>
        </Box>
      ) : (
        <TableContainer
          ref={tableContainerRef}
          sx={{border: '1px solid', borderColor: 'borderColor', borderRadius: '4px'}}
        >
          <Table aria-label="cables" sx={{minWidth: 560}}>
            <TableHead>
              <TableRow>
                <TableCell sx={[headerCellSx, {minWidth: 140}]}>
                  {t('common:manufacturer')}
                </TableCell>
                <TableCell sx={[headerCellSx, {width: 100}]}>
                  {t('common:conductingSection')}
                </TableCell>
                <TableCell sx={[headerCellSx, {width: 120}]}>
                  {t('common:externalDiameter')}
                </TableCell>
                <TableCell sx={[headerCellSx, {width: 100}]}>
                  {t('common:weightKgByMeter')}
                </TableCell>
                <TableCell
                  sx={[headerCellSx, {width: 121}, stickyActionSx, {backgroundColor: 'background.paper'}]}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionCables.map((sectionCable) => (
                <TableRow
                  key={sectionCable.sectionCableId}
                  sx={{
                    bgcolor: sectionCable.hasDeprecatedInfo ? 'deprecatedBackground' : 'transparent',
                    '&:last-child td, &:last-child th': {border: 0}
                  }}
                >
                  <TableCell sx={cellSx}>{sectionCable.manufacturer}</TableCell>
                  <TableCell sx={cellSx}>{sectionCable.conductingSection}</TableCell>
                  <TableCell sx={cellSx}>{formatNumber(sectionCable.diameter)}</TableCell>
                  <TableCell sx={cellSx}>{formatNumber(sectionCable.weight)}</TableCell>
                  <TableCell
                    sx={[
                      cellSx,
                      {width: 121},
                      stickyActionSx,
                      {
                        backgroundColor: sectionCable.hasDeprecatedInfo
                          ? 'deprecatedBackground'
                          : 'background.paper'
                      }
                    ]}
                  >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                      <CableUnits
                        deleteTrayFromSection={deleteTrayFromSection}
                        hasSelectedTray={hasSelectedTray}
                        initialValue={sectionCable.units}
                        onChange={(units: number) =>
                          onCableUnitsChange(sectionCable.sectionCableId, units)
                        }
                        t={t}
                      />
                      <Tooltip title={t('common:deleteCable')}>
                        <IconButton
                          size="small"
                          aria-label="remove"
                          onClick={() => handleCableDelete(sectionCable.sectionCableId)}
                        >
                          <DeleteIcon sx={{color: 'ctaDark'}} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
